import { useEffect, useRef, RefObject } from 'react'

interface UseSwipeOptions {
	onSwipeLeft?: () => void
	onSwipeRight?: () => void
	threshold?: number
	enabled?: boolean
}

// Distance travelled before the gesture commits to an axis
const AXIS_LOCK_DISTANCE = 10

// Gestures slower than this are treated as a press, not a swipe
const MAX_DURATION = 800

/**
 * Adds horizontal swipe/drag navigation to an element for every pointer type
 * (mouse, touch and pen).
 *
 * The gesture locks to an axis after the first few pixels: horizontal claims
 * the pointer, vertical releases it so the page keeps scrolling normally.
 * A completed drag swallows the trailing `click` so a parent click handler
 * (e.g. "open the gallery modal") does not also fire.
 */
export function useSwipe (
	ref: RefObject<HTMLElement | null>,
	{ onSwipeLeft, onSwipeRight, threshold = 50, enabled = true }: UseSwipeOptions
): void {
	// Keep handlers current without re-binding listeners on every render
	const handlersRef = useRef({ onSwipeLeft, onSwipeRight, threshold })
	handlersRef.current = { onSwipeLeft, onSwipeRight, threshold }

	useEffect(() => {
		const el = ref.current
		if (!el || !enabled) return

		let pointerId: number | null = null
		let startX = 0
		let startY = 0
		let startTime = 0
		let isHorizontal = false
		let didSwipe = false

		const reset = () => {
			pointerId = null
			isHorizontal = false
		}

		const onPointerDown = (e: PointerEvent) => {
			// Primary pointer, primary button only — ignores right/middle drags
			if (!e.isPrimary || e.button !== 0) return
			didSwipe = false
			pointerId = e.pointerId
			startX = e.clientX
			startY = e.clientY
			startTime = e.timeStamp
			isHorizontal = false
		}

		const onPointerMove = (e: PointerEvent) => {
			if (pointerId !== e.pointerId) return

			const dx = e.clientX - startX
			const dy = e.clientY - startY

			if (!isHorizontal) {
				if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_DISTANCE) return

				// Vertical intent — hand the gesture back to the browser
				if (Math.abs(dx) <= Math.abs(dy)) {
					reset()
					return
				}

				isHorizontal = true
				// Keeps move/up flowing if the pointer leaves the element mid-drag
				try {
					el.setPointerCapture(e.pointerId)
				} catch {
					// Pointer already released or synthetic — the gesture still completes
				}
			}

			// Suppresses text selection while dragging with a mouse
			e.preventDefault()
		}

		const onPointerUp = (e: PointerEvent) => {
			if (pointerId !== e.pointerId) return

			const dx = e.clientX - startX
			const { onSwipeLeft, onSwipeRight, threshold } = handlersRef.current

			if (
				isHorizontal &&
				e.timeStamp - startTime <= MAX_DURATION &&
				Math.abs(dx) >= threshold
			) {
				didSwipe = true
				if (dx < 0) onSwipeLeft?.()
				else onSwipeRight?.()
			}

			if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
			reset()
		}

		const onPointerCancel = (e: PointerEvent) => {
			if (pointerId !== e.pointerId) return
			reset()
		}

		const onClickCapture = (e: MouseEvent) => {
			if (!didSwipe) return
			didSwipe = false
			e.stopPropagation()
			e.preventDefault()
		}

		// Blocks the browser's native image drag-and-drop, which would abort the gesture
		const onDragStart = (e: Event) => { e.preventDefault() }

		el.addEventListener('pointerdown', onPointerDown)
		el.addEventListener('pointermove', onPointerMove, { passive: false })
		el.addEventListener('pointerup', onPointerUp)
		el.addEventListener('pointercancel', onPointerCancel)
		el.addEventListener('click', onClickCapture, true)
		el.addEventListener('dragstart', onDragStart)

		return () => {
			el.removeEventListener('pointerdown', onPointerDown)
			el.removeEventListener('pointermove', onPointerMove)
			el.removeEventListener('pointerup', onPointerUp)
			el.removeEventListener('pointercancel', onPointerCancel)
			el.removeEventListener('click', onClickCapture, true)
			el.removeEventListener('dragstart', onDragStart)
		}
	}, [ref, enabled])
}
